import { useRef, useState } from "react";
import { ImagePlus, X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { StateSelect } from "@/components/StateSelect";
import { SuburbPicker } from "@/components/SuburbPicker";
import { api, imgUrl, formatErr } from "@/lib/api";
import { CATEGORIES, CONDITIONS, ERAS } from "@/lib/constants";

const Label = ({ children }) => <label className="mb-2 block text-sm font-semibold">{children}</label>;

export function StepPhotos({ f, up }) {
  const ref = useRef();
  const [loading, setLoading] = useState(0);

  const onFiles = async (files) => {
    const list = [...files].slice(0, 8 - f.images.length);
    setLoading(list.length);
    const paths = [];
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      try { paths.push((await api.post("/upload", fd)).data.path); } catch (e) { toast.error(formatErr(e)); }
      setLoading((n) => n - 1);
    }
    up({ images: [...f.images, ...paths] });
  };

  return (
    <div>
      <h2 className="font-display text-xl font-bold">Add photos</h2>
      <p className="mt-1 text-sm text-slate-400">Up to 8. The first photo is your cover.</p>
      <div className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-4">
        {f.images.map((p, i) => (
          <div key={p} data-testid={`sell-photo-${i}`} className="relative aspect-square overflow-hidden rounded-xl bg-[#0f2a33]">
            <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" />
            {i === 0 && <span className="absolute bottom-1.5 left-1.5 rounded-full bg-[#d4f253] px-2 py-0.5 text-[10px] font-bold text-[#0f2a33]">COVER</span>}
            <button data-testid={`sell-photo-remove-${i}`} onClick={() => up({ images: f.images.filter((x) => x !== p) })} className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/60 hover:bg-black/80"><X className="h-4 w-4" /></button>
          </div>
        ))}
        {[...Array(loading)].map((_, i) => <div key={`l${i}`} className="grid aspect-square place-items-center rounded-xl bg-[#0f2a33]"><Loader2 className="h-6 w-6 animate-spin text-[#d4f253]" /></div>)}
        {f.images.length + loading < 8 && (
          <button data-testid="sell-add-photo-btn" onClick={() => ref.current.click()} className="flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-[#d4f253]/50 text-[#d4f253] transition-colors hover:bg-[#d4f253]/10">
            <ImagePlus className="h-7 w-7" /><span className="text-xs font-semibold">Add photo</span>
          </button>
        )}
      </div>
      <input ref={ref} data-testid="sell-photo-input" type="file" accept="image/*" multiple hidden onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
    </div>
  );
}

export function StepDetails({ f, up }) {
  return (
    <div className="space-y-5">
      <h2 className="font-display text-xl font-bold">What are you selling?</h2>
      <div><Label>Title</Label><input data-testid="sell-title-input" className="field" maxLength={100} value={f.title} onChange={(e) => up({ title: e.target.value })} placeholder="e.g. Weber Q BBQ with cover" /></div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div><Label>Category</Label>
          <Select value={f.category} onValueChange={(v) => up({ category: v })}>
            <SelectTrigger data-testid="sell-category-select" className="h-12 rounded-xl bg-white text-[#0f2a33]"><SelectValue placeholder="Choose category" /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`sell-category-option-${c.id}`}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Condition</Label>
          <div className="flex flex-wrap gap-2">
            {CONDITIONS.map((c) => (
              <button key={c.id} type="button" data-testid={`sell-condition-${c.id}`} onClick={() => up({ condition: c.id })} className={`rounded-full px-3 py-2 text-xs font-semibold transition-colors ${f.condition === c.id ? "bg-[#d4f253] text-[#0f2a33]" : "bg-white/10 hover:bg-white/15"}`}>{c.name}</button>
            ))}
          </div>
        </div>
      </div>
      {f.category === "antiques" && (
        <div data-testid="sell-antique-fields" className="grid gap-5 rounded-2xl border border-[#d4f253]/30 p-4 sm:grid-cols-2">
          <div><Label>Era (optional)</Label>
            <Select value={f.era || ""} onValueChange={(v) => up({ era: v })}>
              <SelectTrigger data-testid="sell-era-select" className="h-12 rounded-xl bg-white text-[#0f2a33]"><SelectValue placeholder="When was it made?" /></SelectTrigger>
              <SelectContent>{ERAS.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`sell-era-option-${e.id}`}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Maker / brand (optional)</Label><input data-testid="sell-maker-input" maxLength={80} className="field" value={f.maker || ""} onChange={(e) => up({ maker: e.target.value })} placeholder="e.g. Royal Doulton, Parker, Wedgwood" /></div>
        </div>
      )}
      <div><Label>Description</Label><textarea data-testid="sell-description-input" rows={5} className="field resize-none" value={f.description} onChange={(e) => up({ description: e.target.value })} placeholder="Size, age, any marks, why you're selling…" /></div>
    </div>
  );
}

export function StepLocation({ f, up }) {
  return (
    <div className="space-y-5">
      <h2 className="font-display text-xl font-bold">Where & how much?</h2>
      <div className="grid gap-5 sm:grid-cols-[auto_1fr_120px]">
        <div><Label>State</Label><StateSelect value={f.state} onChange={(v) => up({ state: v })} testId="sell-state-select" light /></div>
        <div><Label>Suburb</Label><SuburbPicker testId="sell-suburb" value={f.suburb} state={f.state} placeholder="e.g. Southport" onChange={(v) => up({ suburb: v })} onPick={(o) => up({ suburb: o.suburb, postcode: o.postcode, state: o.state })} /></div>
        <div><Label>Postcode</Label><input data-testid="sell-postcode-input" className="field" maxLength={4} value={f.postcode} onChange={(e) => up({ postcode: e.target.value.replace(/\D/g, "") })} placeholder="4215" /></div>
      </div>
      <div className="flex items-center justify-between rounded-xl bg-[#0f2a33] p-4">
        <div><p className="text-sm font-semibold">Giving it away free?</p><p className="text-xs text-slate-400">Free items get snapped up fast.</p></div>
        <Switch data-testid="sell-free-switch" checked={f.is_free} onCheckedChange={(v) => up({ is_free: v })} />
      </div>
      {!f.is_free && (
        <div><Label>Price (AUD)</Label>
          <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-[#0f2a33]">$</span>
            <input data-testid="sell-price-input" type="number" min="0" className="field !pl-9" value={f.price} onChange={(e) => up({ price: e.target.value })} placeholder="0" />
          </div>
        </div>
      )}
      <div className="flex gap-3 rounded-xl border border-[#d4f253]/30 bg-[#d4f253]/5 p-4 text-sm">
        <ShieldCheck className="h-5 w-5 shrink-0 text-[#d4f253]" />
        <p className="text-slate-300">Buyers pay you directly at pick-up (cash or PayID). ISoldIt never handles payments and charges no fees.</p>
      </div>
    </div>
  );
}
